import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { AnalyticsPaginationProps } from "../types"
import { useTranslation } from "react-i18next"

export const AnalyticsPagination = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  startRecord,
  endRecord,
  onPageChange,
  onPageSizeChange,
}: AnalyticsPaginationProps) => {
  const {t} = useTranslation("analytics")
  return (
    <>
      {/* Table Controls */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {totalRecords > 0 ? (
            <>
              {t("analytics:pagination.showing")} <span className="font-medium">{startRecord}</span> {t("analytics:pagination.to")}{" "}
              <span className="font-medium">{endRecord}</span> {t("analytics:pagination.of")}{" "}
              <span className="font-medium">{totalRecords}</span> {t("analytics:pagination.sessions")}
            </>
          ) : (
            t("analytics:pagination.noSessions")
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("analytics:pagination.rows")}</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              onPageSizeChange(Number(value))
            }}
          >
            <SelectTrigger className="w-16 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pagination */}
      {totalRecords > 0 && totalPages > 1 && (
        <>
          <Separator />
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              {t("analytics:pagination.previous")}
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                if (pageNum > totalPages) return null
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    className="w-10"
                  >
                    {pageNum}
                  </Button>
                )
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              {t("analytics:pagination.next")}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </>
  )
}
